/** @format */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, makeStyles, Typography } from '@material-ui/core';
import I18n from '../../../components/Translate/I18n';
import Card from '@material-ui/core/Card';
import CardHeader from '../../../components/Card/CardHeader';
import CardBody from '../../../components/Card/CardBody';
import CustomTextButtom from '../../../components/Buttons/CustomTextButton';
import { useDispatch } from 'react-redux';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import adescomStyle from '../../../assets/jss/adescom/adescomStyle';
import { InvoiceDataInterface } from '../../../interfaces/platform/InvoiceDataInterface';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import { InvoiceInterface } from '../../../interfaces/platform/InvoiceInterface';
import { documentConstants } from '../../../modules/document/actions';
import { FilterListReducerFieldName } from '../../../modules/filter/FilterListReducers';
import { CurrencyHelper } from '../../../helpers/CurrencyHelper';
import { useVats } from '../../../modules/document/useVats';

export type CostReportModalProps = {
    openView: boolean;
    fullWidth: boolean;
    title?: string;
    selectedInvoice: InvoiceInterface | null;
    closeCallback: () => void;
    showMainLoader(show: boolean): void;
};

const _ = require('underscore');

export const CostReportModal = (props: CostReportModalProps): React.ReactElement => {
    const dispatch = useDispatch();
    const useStyle = makeStyles(adescomStyle);
    const classes = useStyle();
    const itemLoader: ItemLoaders = useMemo(() => {
        return new ItemLoaders(dispatch);
    }, [dispatch]);
    const [records, setRecords] = useState<any>(null);
    const [noRecords, setNoRecords] = useState<boolean>(false);
    const { showMainLoader, selectedInvoice } = props;
    const invoiceData = useMemo((): Array<InvoiceDataInterface> => {
        if (!selectedInvoice || !selectedInvoice.document || !selectedInvoice.document.requestParams) {
            return [];
        }
        const params = JSON.parse(selectedInvoice.document.requestParams);
        return params.invoiceData;
    }, [selectedInvoice]);
    const [warning, setWarning] = useState<boolean>(false);
    const [difference, setDifference] = useState<number>(0.0);
    const [currencySign, setCurrencySign] = useState<string>('PLN');
    const { vats, vatsSelect, triedLoading } = useVats();


    const firstLoad = useCallback((): void => {
        if (!selectedInvoice) {
            return;
        }

        showMainLoader(true);

        itemLoader.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/cost_reports/' + selectedInvoice.id)),
            documentConstants.COST_REPORT_LIST,
            [],
            [],
            {
                onSuccess: (items: any): void => {
                    setRecords(null);
                    setRecords(items);
                    let warning = false;
                    let sumRemote = 0.0;
                    let sumLocal = 0.0;
                    if (items && items['currencySign']) {
                        setCurrencySign(items['currencySign'].toUpperCase());
                    }
                    if (items && items['hydra:member'] && items['hydra:member'].length > 0) {
                        _.each(items['hydra:member'], (item: InvoiceDataInterface) => {
                            if (item.recordName !== 'Suma') {
                                sumRemote += parseFloat(parseFloat(String(item.noTaxAmount)).toFixed(2)) || 0.0;
                            }
                        });

                        _.each(invoiceData, (item: InvoiceDataInterface) => {
                            if (item.recordName !== 'Suma') {
                                sumLocal += parseFloat(parseFloat(String(item.noTaxAmount)).toFixed(2)) || 0.0;
                            }
                        });
                    }

                    sumLocal = parseFloat(sumLocal.toFixed(2));
                    sumRemote = parseFloat(sumRemote.toFixed(2));

                    if (sumRemote !== sumLocal) {
                        warning = true;
                    }

                    let difference = ((sumLocal - sumRemote) * 100) / sumLocal || 0.0;

                    if (warning) {
                        if (difference < 0) {
                            difference *= -1;
                        }
                    }

                    if (warning && difference !== 0.0) {
                        warning = false;
                        difference = 0.0;
                    }

                    setDifference(Number(difference.toFixed(2)));
                    setWarning(warning);
                    setNoRecords(false);
                    showMainLoader(false);
                    dispatch({
                        type: FilterListReducerFieldName.HEADER_FILTER_MANAGE,
                        showRender: true,
                    });
                },
                onError: (e: any): void => {
                    setNoRecords(true);
                },
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    }, [showMainLoader, setNoRecords, itemLoader, dispatch, setRecords, invoiceData, selectedInvoice]);

    useEffect(() => {
        firstLoad();
    }, [firstLoad]);

    return (
        <Dialog
            aria-labelledby="alert-dialog-slide-title"
            aria-describedby="alert-dialog-slide-description"
            keepMounted
            open={props.openView}
            fullWidth={props.fullWidth}
            maxWidth="lg"
        >
            <DialogTitle>
                {props.title === null && ''}
                {typeof props.title === 'string' ? <I18n trans={props.title} /> : props.title}
            </DialogTitle>
            <DialogContent>
                {noRecords && (
                    <GridContainer>
                        <GridItem xs={4} />
                        <GridItem xs={4}>
                            <div
                                style={{
                                    textAlign: 'center',
                                    fontWeight: 'bold',
                                    color: 'white',
                                    borderRadius: '10px',
                                    borderColor: 'red',
                                    backgroundColor: '#f44336',
                                    padding: '10px',
                                }}
                            >
                                <I18n trans={'documents:invoices.no_system_records'} />
                            </div>
                        </GridItem>
                        <GridItem xs={4} />
                    </GridContainer>
                )}
                {warning && (
                    <GridContainer>
                        <GridItem xs={4} />
                        <GridItem xs={4}>
                            <div
                                style={{
                                    textAlign: 'center',
                                    fontWeight: 'bold',
                                    color: 'white',
                                    borderRadius: '10px',
                                    borderColor: 'red',
                                    backgroundColor: '#f44336',
                                    padding: '10px',
                                }}
                            >
                                <I18n trans={'documents:invoices.cost_values_dont_match'} />
                                <br />
                                <I18n trans={'documents:invoices.cost_difference_is'} valueObject={{ difference: difference }} />
                            </div>
                        </GridItem>
                        <GridItem xs={4} />
                    </GridContainer>
                )}
                <Card>
                    <CardHeader>
                        <Typography variant="subtitle2">
                            <I18n trans="documents:invoices.system_records" />
                        </Typography>
                    </CardHeader>
                    <CardBody>
                        <GridContainer>
                            <GridItem xs={6} style={{ fontWeight: 'bold' }}>
                                <I18n trans={'documents:invoices.record_name'} />
                            </GridItem>
                            <GridItem xs={6} style={{ fontWeight: 'bold' }}>
                                <I18n trans={'documents:invoices.no_tax_amount'} />
                            </GridItem>
                            {records &&
                                records['hydra:member'] &&
                                records['hydra:member'].map((record: InvoiceDataInterface) => {
                                    const style: any = {};
                                    if (record.recordName === 'Suma') {
                                        style.fontWeight = 'bold';
                                        style.backgroundColor = 'rgba(0, 0, 0, 0.2)';
                                    }
                                    return (
                                        <>
                                            <GridItem style={style} xs={6}>
                                                {record.recordName}
                                            </GridItem>
                                            <GridItem style={style} xs={6}>
                                                {CurrencyHelper.numericToStandardPln(record.noTaxAmount)} { currencySign }
                                            </GridItem>
                                        </>
                                    );
                                })}
                        </GridContainer>
                    </CardBody>

                    <CardHeader>
                        <Typography variant="subtitle2">
                            <I18n trans="documents:invoices.cost_invoice_records" />
                        </Typography>
                    </CardHeader>
                    <CardBody>
                        <GridContainer>
                            <GridItem xs={6} style={{ fontWeight: 'bold' }}>
                                <I18n trans={'documents:invoices.record_name'} />
                            </GridItem>
                            <GridItem xs={6} style={{ fontWeight: 'bold' }}>
                                <I18n trans={'documents:invoices.no_tax_amount'} />
                            </GridItem>
                            {invoiceData &&
                                invoiceData.map((record: InvoiceDataInterface) => {
                                    const style: any = {};
                                    if (record.recordName === 'Suma') {
                                        style.fontWeight = 'bold';
                                        style.backgroundColor = 'rgba(0, 0, 0, 0.2)';
                                    }
                                    return (
                                        <>
                                            <GridItem style={style} xs={6}>
                                                {record.recordName}
                                            </GridItem>
                                            <GridItem style={style} xs={6}>
                                                {CurrencyHelper.numericToStandardPln(record.noTaxAmount)} { currencySign }
                                            </GridItem>
                                        </>
                                    );
                                })}
                        </GridContainer>
                    </CardBody>
                </Card>
            </DialogContent>
            <DialogActions>
                <CustomTextButtom callback={props.closeCallback} label="general:common.close" />
            </DialogActions>
        </Dialog>
    );
};

export default CostReportModal;
